import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  Divider,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import eventService from '../../services/eventService';

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(false);

  useEffect(() => {
    fetchEvent();
    fetchStats();
  }, [id]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await eventService.getEventById(id);
      if (response.success) {
        setEvent(response.data.event);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch event');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await eventService.getEventStats(id);
      if (response.success) {
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleDelete = async () => {
    try {
      await eventService.deleteEvent(id);
      navigate('/admin/events');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete event');
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      published: 'success',
      cancelled: 'error',
      completed: 'info'
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !event) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Event not found'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/admin/events')}
          >
            Back to Events
          </Button>
          <Chip label={event.status} color={getStatusColor(event.status)} />
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/events/${id}/edit`)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialog(true)}
          >
            Delete
          </Button>
        </Box>
      </Box>

      {/* Banner */}
      {event.banner?.url && (
        <Paper elevation={0} sx={{ mb: 3, overflow: 'hidden', borderRadius: 2 }}>
          <img
            src={eventService.getImageUrl(event.banner.url)}
            alt={event.eventName}
            style={{ width: '100%', maxHeight: '400px', objectFit: 'cover' }}
          />
        </Paper>
      )}

      {/* Event Info */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          {event.eventName}
        </Typography>

        <Box display="flex" gap={2} mb={3}>
          <Chip label={event.category} variant="outlined" />
          {event.tags?.map((tag, index) => (
            <Chip key={index} label={tag} size="small" variant="outlined" />
          ))}
        </Box>

        <Typography variant="body1" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
          {event.description}
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3
          }}
        >
          <Box display="flex" alignItems="start" gap={2}>
            <CalendarIcon color="action" />
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Date & Time
              </Typography>
              <Typography variant="body1">
                {event.dates?.[0]?.startDate && formatDate(event.dates[0].startDate)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {event.dates?.[0]?.startTime} - {event.dates?.[0]?.endTime || 'TBD'}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="start" gap={2}>
            <LocationIcon color="action" />
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Location
              </Typography>
              <Typography variant="body1">{event.location?.venue}</Typography>
              <Typography variant="body2" color="text.secondary">
                {event.location?.address}, {event.location?.city}, {event.location?.state}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Statistics */}
      {stats && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
            gap: 3,
            mb: 3
          }}
        >
          <Card>
            <CardContent>
              <Typography variant="h4" color="primary" gutterBottom>
                {stats.totalRSVPs}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total RSVPs
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h4" color="success.main" gutterBottom>
                {stats.totalAttended}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Attended
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h4" color="info.main" gutterBottom>
                {stats.attendanceRate}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Attendance Rate
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h4" color="warning.main" gutterBottom>
                {stats.capacityUsed}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Capacity Used
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Actions */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Quick Actions
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            startIcon={<PeopleIcon />}
            onClick={() => navigate(`/admin/events/${id}/attendees`)}
          >
            View Attendees
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(`/admin/analytics?eventId=${id}`)}
          >
            View Analytics
          </Button>
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{event.eventName}"? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EventDetail;
